/**
 * ============================================================================
 * headingId — Algoritmo compartilhado para gerar IDs estáveis de headings
 *              a partir do texto. Usado tanto pela extração de headings do
 *              JSON (useDocHeadings) quanto pelo plugin ProseMirror que
 *              aplica o `id` nos <h1..h4> renderizados no DOM. Garantir que
 *              os dois lados produzem o MESMO id é o que faz o clique do
 *              Outline funcionar de forma robusta.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Extraído de useDocHeadings/HeadingIdExtension.
 * ============================================================================
 */

// Slug determinístico: minúsculas, sem acentos, sem pontuação, hífens no lugar de espaços.
export function slugifyHeading(text: string): string {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

// djb2 curto — usado como sufixo do slug para desambiguar títulos iguais.
export function shortHashHeading(text: string, salt = 0): string {
  let hash = 5381 + salt;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash) + text.charCodeAt(i);
    hash |= 0;
  }
  return (hash >>> 0).toString(36).slice(0, 5);
}

/**
 * Calcula o id final do heading combinando slug + hash.
 * `occurrenceIndex` é 1-based e serve para diferenciar títulos repetidos
 * (o segundo "Introdução" pega salt=2, terceiro salt=3, etc.).
 */
export function computeHeadingId(text: string, occurrenceIndex = 1): string {
  const base = slugifyHeading(text) || "sec";
  const salt = occurrenceIndex <= 1 ? 0 : occurrenceIndex;
  return `${base}-${shortHashHeading(text, salt)}`;
}
